import { Component, OnInit } from "@angular/core";
import { RdsService } from "../../services/rds.service";
import { GroupSelectedService } from "../../services/selected.group.service";
import {
  EMPTY,
  Observable,
  Subject,
  interval,
  catchError,
  switchMap,
  takeUntil,
  tap,
  startWith,
  takeWhile,
  finalize,
} from "rxjs";
import { UserState } from "src/app/store/user/user.state";
import { Store, select } from "@ngrx/store";
import { getUser } from "src/app/store/user/user.selector";
import { Rds } from "../../models/rds";

@Component({
  standalone: false,
  selector: "app-liste-rds",
  templateUrl: "./liste-rds.component.html",
  styleUrls: ["./liste-rds.component.scss"],
})
export class ListeRdsComponent implements OnInit {
  displayedColumns: string[] = [
    "DBInstanceStatus",
    "DBName",
    "AvailabilityZone",
    "DBInstanceIdentifier",
    "actions",
  ];
  displayedColumnsName: string[] = ["", "Nom", "Région", "Instance Id", ""];
  dataSource: any;
  loading: boolean = false;
  states: any = {
    starting: { icon: "hourglass_top", label: "Démarrage en cours" },
    pending: { icon: "hourglass_top", label: "Démarrage en cours" },
    available: { icon: "check_circle", label: "Disponible" },
    running: { icon: "check_circle", label: "En fonctionnement" },
    "shutting-down": { icon: "hourglass_bottom", label: "Arrêt en cours" },
    terminated: { icon: "cancel", label: "Terminée" },
    stopping: { icon: "hourglass_bottom", label: "Arrêt en cours" },
    rebooting: { icon: "hourglass_top", label: "Redémarrage en cours" },
    stopped: { icon: "stop_circle", label: "Arrêtée" },
    "configuring-enhanced-monitoring": { icon: "hourglass_top", label: "Configuration en cours" },
  };
  all: boolean = false;
  selectedGroup: any = null;
  user$: Observable<UserState>;
  user_actions: any[] = [];
  user_groups: any[] = [];
  private onDestroy$ = new Subject<void>();
  private stopPolling$ = new Subject<void>();
  private isPollingPaused = false;

  // Ajout de la constante pour l'intervalle de rafraîchissement (en ms)
  private readonly REFRESH_INTERVAL = 30000; // 30 secondes

  // Ajouter une Map pour suivre les instances en cours de mise à jour
  private updatingInstances = new Map<string, string>();

  constructor(
    private groupSelected: GroupSelectedService,
    private rdsService: RdsService,
    private store: Store<{ user: UserState }>,
  ) {
    this.user$ = this.store.pipe(select(getUser));
    this.user$.subscribe(
      (user) => {
        this.updateUserActions(
          user,
          "start",
          "play_circle",
          "primary",
          this.startInstance,
          this.isInstanceRunning,
        );
        this.updateUserActions(
          user,
          "stop",
          "stop_circle",
          "warn",
          this.stopInstance,
          this.isInstanceRunning,
        );
      },
      (error) => console.error(error),
    );
  }

  updateUserActions(
    user: any,
    actionName: string,
    icon: string,
    color: string,
    handler: Function,
    condition: Function,
  ) {
    let action: any = {
      name: actionName,
    };

    if (this.isPermitted(user, actionName.toUpperCase())) {
      action = {
        name: actionName,
        icon: icon,
        color: color,
        handler: handler,
        condition: condition,
      };
    }

    const actionIndex = this.user_actions.findIndex(
      (existingAction) => existingAction.name === actionName,
    );

    if (actionIndex < 0) {
      this.user_actions.push(action);
    } else {
      this.user_actions[actionIndex] = action;
    }
  }

  ngOnInit(): void {
    this.loading = true;
    this.startGeneralPolling();
  }

  private startGeneralPolling() {
    if (this.isPollingPaused) {
      return;
    }

    const fetchSource$ = this.groupSelected.selected$.pipe(
      takeUntil(this.onDestroy$),
      tap(() => (this.loading = true)),
      switchMap((group) => {
        this.selectedGroup = group;
        this.all = false;

        return interval(this.REFRESH_INTERVAL).pipe(
          startWith(0),
          takeUntil(this.stopPolling$),
          switchMap(() => {
            if (this.isPollingPaused) {
              return EMPTY;
            }
            if (group && group.group) {
              return this.rdsService.getByGroup(group.group);
            } else {
              this.all = true;
              return this.rdsService.get();
            }
          })
        );
      }),
      catchError((err) => {
        console.error("Erreur lors du chargement des instances RDS:", err);
        this.loading = false;
        return EMPTY;
      })
    );

    fetchSource$.subscribe((rdss) => {
      if (!this.isPollingPaused) {
        this.dataSource = rdss;
        this.loading = false;
      }
    });
  }

  private pauseGeneralPolling() {
    this.isPollingPaused = true;
    this.stopPolling$.next();
  }

  private resumeGeneralPolling() {
    this.isPollingPaused = false;
    this.startGeneralPolling();
  }

  ngOnDestroy() {
    this.stopPolling$.next();
    this.stopPolling$.complete();
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  renderStatusIcon(data: string, type: any, row: any, meta: any) {
    return data == "running"
      ? '<mat-icon role="img" style="color: #1d8102;" class="mat-icon notranslate mat-tooltip-trigger material-icons mat-icon-no-color caaks-success" aria-hidden="true" data-mat-icon-type="font" aria-describedby="cdk-describedby-message-73" cdk-describedby-host="0"> fiber_manual_record </mat-icon>'
      : '<mat-icon role="img" class="mat-icon notranslate mat-tooltip-trigger material-icons mat-icon-no-color " aria-hidden="true" data-mat-icon-type="font" aria-describedby="cdk-describedby-message-73" cdk-describedby-host="0"> fiber_manual_record </mat-icon>';
  }

  getResourceStateDesc(state: string) {
    return this.states[state];
  }

  getDisplayedResourceState(resource: Rds): string {
    return this.updatingInstances.get(resource.DBInstanceIdentifier) || resource.DBInstanceStatus;
  }

  getResourceStateLabel(state: string): string {
    return this.states[state]?.label || state;
  }

  getRowActions() {
    return this.user_actions;
  }

  isPermitted(user: any, action: string): boolean {
    console.log("Check if isPermitted", user, action);

    if (!user || !user.roles || !Array.isArray(user.roles)) {
      console.warn("Invalid user or roles structure");
      return false;
    }

    try {
      for (let role of user.roles) {
        if (!role || !role.permissions || !Array.isArray(role.permissions)) {
          console.warn("Invalid role or permissions structure");
          continue;
        }

        const hasPermission = role.permissions.some(
          (permission: any) =>
            permission.policies === action || permission.name === "full",
        );

        if (hasPermission) {
          console.log("User has permission");
          return true;
        }
      }
    } catch (e) {
      console.error("Error in isPermitted", e);
      return false;
    }

    console.log("User does not have permission");
    return false;
  }

  isInstanceRunning(instance: any) {
    return instance[0] == "running" || instance[0] == "available";
  }

  // Méthode pour suivre l'état d'une instance jusqu'à ce qu'elle atteigne l'état désiré
  private pollInstanceState(instanceId: string, targetState: 'available' | 'stopped'): Observable<any> {
    // Pause le polling général pendant le polling spécifique
    this.pauseGeneralPolling();

    return interval(5000).pipe(
      startWith(0),
      switchMap(() => {
        if (this.selectedGroup && this.selectedGroup.group) {
          return this.rdsService.getByGroup(this.selectedGroup.group);
        } else {
          return this.rdsService.get();
        }
      }),
      tap(instances => {
        this.dataSource = instances;
        const instance = instances.find((i: Rds) => i.DBInstanceIdentifier === instanceId);
        if (instance) {
          console.log(`État actuel de l'instance ${instanceId}: ${instance.DBInstanceStatus}`);
        }
      }),
      takeWhile(instances => {
        const instance = instances.find((i: Rds) => i.DBInstanceIdentifier === instanceId);
        if (!instance) {
          return false;
        }
        const currentState = instance.DBInstanceStatus;
        const isUpdating = this.updatingInstances.has(instanceId);

        const shouldContinue = currentState !== targetState &&
                             currentState !== 'error' &&
                             isUpdating;

        if (!shouldContinue) {
          this.updatingInstances.delete(instanceId);
        }

        return shouldContinue;
      }, true),
      takeUntil(this.onDestroy$),
      finalize(() => {
        this.updatingInstances.delete(instanceId);
        // Reprendre le polling général une fois terminé
        this.resumeGeneralPolling();
      })
    );
  }

  startInstance(event: any, instance: any) {
    if (this.updatingInstances.get(instance.DBInstanceIdentifier)) {
      console.log('Instance déjà en cours de mise à jour');
      return;
    }

    this.updatingInstances.set(instance.DBInstanceIdentifier, "starting");

    // Appeler le service pour démarrer l'instance
    this.rdsService.start(instance).pipe(
      // Si le démarrage est confirmé et réussi, commencer à suivre l'état
      switchMap((response) => {
        if (response && response.status) {
          return this.pollInstanceState(instance.DBInstanceIdentifier, 'available');
        }
        this.updatingInstances.delete(instance.DBInstanceIdentifier);
        return EMPTY;
      }),
      catchError(error => {
        console.error('Erreur lors du démarrage de l\'instance:', error);
        this.updatingInstances.delete(instance.DBInstanceIdentifier);
        return EMPTY;
      })
    ).subscribe();
  }

  stopInstance(event: any, instance: any) {
    if (this.updatingInstances.get(instance.DBInstanceIdentifier)) {
      console.log('Instance déjà en cours de mise à jour');
      return;
    }

    this.updatingInstances.set(instance.DBInstanceIdentifier, "stopping");

    // Appeler le service pour arrêter l'instance
    this.rdsService.stop(instance).pipe(
      // Si l'arrêt est confirmé et réussi, commencer à suivre l'état
      switchMap((response) => {
        if (response && response.status) {
          return this.pollInstanceState(instance.DBInstanceIdentifier, 'stopped');
        }
        this.updatingInstances.delete(instance.DBInstanceIdentifier);
        return EMPTY;
      }),
      catchError(error => {
        console.error('Erreur lors de l\'arrêt de l\'instance:', error);
        this.updatingInstances.delete(instance.DBInstanceIdentifier);
        return EMPTY;
      })
    ).subscribe();
  }

  getApplicableActions(resource: any) {
    if (this.updatingInstances.has(resource.DBInstanceIdentifier)) {
      return [];
    }
    return this.getRowActions().filter((action) => {
      if (action.name === "start") {
        return (
          resource["DBInstanceStatus"] !== "running" &&
          resource["DBInstanceStatus"] !== "available" &&
          resource["DBInstanceStatus"] !== "pending"
        );
      } else if (action.name === "stop") {
        return (
          resource["DBInstanceStatus"] === "running" ||
          resource["DBInstanceStatus"] === "available"
        );
      }
      return false;
    });
  }

  renderTableActions(data: any, type: any, row: any, meta: any) {
    if (
      data.DBInstanceStatus != "running" &&
      data.DBInstanceStatus != "available"
    ) {
      return '<button mat-icon-button matTooltip="Basic" aria-label="Démarrer cette ressource">\
        <span class="material-symbols-outlined">play_circle</span>\
        </button>';
    }

    return "";
  }
}